import apiClient from './api/client';
import ENDPOINTS from './api/endpoints';

/**
 * Menu Item Service
 * Handles all menu-item related API operations
 */
export const itemService = {
  // ✅ Get all menu items (optional query params)
  getAll: async (params) => {
    try {
      const response = await apiClient.get(ENDPOINTS.MENU.GET_ALL, { params });
      return response; // already response.data due to interceptor
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
      throw error;
    }
  },

  // ✅ Get single item by ID
  getById: async (id) => {
    try {
      const response = await apiClient.get(ENDPOINTS.MENU.GET_BY_ID(id));
      return response;
    } catch (error) {
      console.error(`Failed to fetch menu item ${id}:`, error);
      throw error;
    }
  },

  // ✅ Create new item
  create: async (itemData) => {
    try {
      const response = await apiClient.post(ENDPOINTS.MENU.CREATE, itemData);
      return response;
    } catch (error) {
      console.error('Failed to create menu item:', error);
      throw error;
    }
  },

  // ✅ Update item
  update: async (id, updates) => {
    try {
      const response = await apiClient.put(ENDPOINTS.MENU.UPDATE(id), updates);
      return response;
    } catch (error) {
      console.error(`Failed to update menu item ${id}:`, error);
      throw error;
    }
  },

  // ✅ Delete item
  remove: async (id) => {
    try {
      const response = await apiClient.delete(ENDPOINTS.MENU.DELETE(id));
      return response;
    } catch (error) {
      console.error(`Failed to delete menu item ${id}:`, error);
      throw error;
    }
  },

  // ✅ Bulk update prices for multiple menu items and variants
  bulkUpdatePrices: async (payload) => {
    try {
      const response = await apiClient.post(ENDPOINTS.MENU.BULK_PRICE_UPDATE, payload);
      return response;
    } catch (error) {
      console.error('Failed to bulk update prices:', error);
      throw error;
    }
  },

  // ✅ Upload photo for menu item (uses FileUploadController)
  uploadPhoto: async (id, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        ENDPOINTS.UPLOADS.MENU_ITEM_IMAGE(id),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to upload photo for menu item ${id}:`, error);
      throw error;
    }
  },

  // ✅ Get photo URL for menu item (public endpoint, no auth needed)
  // API now returns full relative URL path (e.g., /uploads/menu-items/filename.jpg)
  getPhotoUrl: (imageUrl) => {
    if (!imageUrl) return null;
    const baseUrl = apiClient.defaults.baseURL || '';
    // imageUrl from API is already the full relative path
    return `${baseUrl}${imageUrl}`;
  },

  // ✅ Get items by category
  getByCategory: async (categoryId, params) => {
    try {
      const response = await apiClient.get(ENDPOINTS.MENU.GET_BY_CATEGORY(categoryId), { params });
      return response;
    } catch (error) {
      console.error(`Failed to fetch items for category ${categoryId}:`, error);
      throw error;
    }
  },

  uploadBulkMenu: async (file, version = 'V1') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(
        `${ENDPOINTS.MENU.BULK_UPLOAD}?version=${version}`,
        formData,
        {
          headers: {
            'Content-Type': undefined,
          },
        }
      );
      return response;
    } catch (error) {
      console.error('Failed to upload bulk menu:', error);
      throw error;
    }
  },

  downloadTemplate: async (version = 'V1') => {
    try {
      const response = await apiClient.get(
        `${ENDPOINTS.MENU.DOWNLOAD_TEMPLATE}?version=${version}`,
        {
          responseType: 'blob',
        }
      );
      return response;
    } catch (error) {
      console.error('Failed to download template:', error);
      throw error;
    }
  },
};

export default itemService;
